/**
 * Manifest.ts
 * Module for managing the manifest file
 */

import { IFile } from "./IFile";
import { FeedbackType, SDK_VERSION_PREFIX } from "./../../Constants";
import * as Utils from "./../../Utils";
import Version from "./../../Version";
import * as Window from "./../../Window/Window";

export class Manifest implements IFile {
	private manifestData: IManifestData;

	public constructor(manifestType: string, appId: number, submitTime: string, type: ManifestFeedbackType,
		clientFeedbackId: string, applicationGroup: IManifestDataApplication, telemetryGroup: IManifestDataTelemetry,
		webGroup: IManifestDataWeb, retentionDurationDays: number) {
		this.manifestData = {
			appId: appId,
			application: applicationGroup,
			clientFeedbackId: clientFeedbackId,
			manifestType: manifestType,
			source: "Client",
			submitTime: submitTime,
			telemetry: telemetryGroup,
			type: sanitizeType(type),
			web: webGroup,
			sdkVersion: SDK_VERSION_PREFIX
				+ (Window.get().OfficeBrowserFeedback.sdkVersion !== undefined ?
					Window.get().OfficeBrowserFeedback.sdkVersion : Version),
			retentionDurationDays: retentionDurationDays,
		};
	}

	public getExtendedManifestData(): string {
		return this.manifestData.application.extendedManifestData;
	}

	public setComment(comment: string) {
		this.manifestData.comment = comment;
	}

	public setEmail(email: string) {
		this.manifestData.email = email;
	}

	public setCategory(category: string) {
		this.manifestData.telemetry = this.manifestData.telemetry || {};
		this.manifestData.telemetry.featureArea = category;
	}

	public setAppData(appData: string) {
		this.manifestData.application = this.manifestData.application || {};
		this.manifestData.application.appData = appData;
	}

	public setExtendedManifestData(extendedManifestData: string) {
		this.manifestData.application = this.manifestData.application || {};
		this.manifestData.application.extendedManifestData = extendedManifestData;
	}

	public setClientFeedbackId(clientFeedbackId: string) {
		this.manifestData.clientFeedbackId = clientFeedbackId;
	}

	public getClientFeedbackId(): string {
		return this.manifestData.clientFeedbackId;
	}

	public setComplianceInformation(authenticationType: string, ageGroup: string, sendFeedbackPolicyValue: string,
		sendSurveyPolicyValue: string, screenshotPolicyValue: string, emailPolicyValue: string) {
		this.manifestData.complianceChecks = {
			authenticationType: authenticationType,
			ageGroup: ageGroup,
			policyAllowFeedback: sendFeedbackPolicyValue,
			policyAllowSurvey: sendSurveyPolicyValue,
			policyAllowScreenshot: screenshotPolicyValue,
			policyAllowContact: emailPolicyValue,
		};
	}

	/**
	 * Set freeform custom values in manifest. Throws if values are already set.
	 * @param values the values as a JS object
	 */
	public setValues(values: object) {
		if (values) {
			for (let field in values) {
				if (values.hasOwnProperty(field)) {
					if (this.manifestData.hasOwnProperty(field)) {
						(<any> this.manifestData)[field] = Utils.overrideValues((<any> values)[field], (<any> this.manifestData)[field]);
					} else {
						(<any> this.manifestData)[field] = (<any> values)[field];
					}
				}
			}
		}
	}

	public getContent(): Blob {
		return new Blob([JSON.stringify(this.manifestData)], { type: "application/json" });
	}

	public appendUxId(UxId: string): void {
		if (!Utils.isNOU(UxId) && Utils.isString(UxId) && UxId !== "") {
			this.manifestData.sdkVersion = this.manifestData.sdkVersion + "," + UxId;
		}
	}
}

function sanitizeType(type: ManifestFeedbackType): string {
	if (type === "Survey") {
		return type;
	} else {
		return FeedbackType[type];
	}
}

export type ManifestFeedbackType = FeedbackType | "Survey";

/**
 * Interface for the manifest data
 */
export interface IManifestData {
	// #region "Required properties"
	manifestType: string;
	appId: number;
	submitTime: string;
	source: string;
	type: string;
	clientFeedbackId: string;
	sdkVersion: string;
	// #endregion "Required properties"
	comment?: string;
	email?: string;
	rating?: number;
	survey?: IManifestDataSurvey;
	web?: IManifestDataWeb;
	application?: IManifestDataApplication;
	telemetry?: IManifestDataTelemetry;
	complianceChecks?: IManifestComplianceChecks;
	retentionDurationDays?: number;
}

/**
 * Interface for survey related properties in manifest
 */
export interface IManifestDataSurvey {
	surveyId: string;
}

/**
 * Interface for application related properties in manifest
 */
export interface IManifestDataApplication {
	appData?: string;
	extendedManifestData?: string;
	feedbackTenant?: string;
}

/**
 * Interface for telemetry related properties in manifest
 */
export interface IManifestDataTelemetry {
	audience?: string;
	audienceGroup?: string;
	channel?: string;
	deviceId?: string;
	deviceType?: string;
	errorClassification?: string;
	errorCode?: string;
	errorName?: string;
	featureArea?: string;
	flights?: string;
	flightSource?: string;
	fundamentalArea?: string;
	installationType?: string;
	isLogIncluded?: boolean;
	isUserSubscriber?: boolean;
	loggableUserId?: string;
	officeArchitecture?: string;
	officeBuild?: string;
	officeEditingLang?: number;
	officeUILang?: number;
	osBitness?: number;
	osBuild?: string;
	osUserLang?: number;
	platform?: string;
	processSessionId?: string;
	ringId?: number;
	sku?: string;
	sourceContext?: string;
	systemProductName?: string;
	systemManufacturer?: string;
	tenantId?: string;
}

/**
 * Interface for web related properties in manifest
 */
export interface IManifestDataWeb {
	browser?: string;
	browserVersion?: string;
	dataCenter?: string;
	sourcePageName?: string;
	sourcePageURI?: string;
}

/**
 * Interface for compliance checks related properties in manifest
 */
export interface IManifestComplianceChecks {
	authenticationType?: string;
	ageGroup?: string;
	policyAllowFeedback?: string;
	policyAllowSurvey?: string;
	policyAllowScreenshot?: string;
	policyAllowContact?: string;
}
